<script setup lang="ts">
/**
 * The company's own KPIs on HQ (#895): what the CEO sets and what HQ shows first. Reads and writes
 * `/v1/enterprise/kpi-targets` — the same endpoint the MCP server's `os.get_enterprise_kpis` /
 * `os.set_enterprise_kpis` call — so a KPI set here, through the API, or by an agent is one KPI with one
 * set of rules. A company that never set one sees its old free-text metric lines here as KPIs waiting for
 * a target, so nothing already written is lost.
 */
import { Button } from '@zerohuman/ui'
import { onMounted, ref } from 'vue'
import { api } from '@/api'
import type { Kpi } from '@/lib/kpi'
import KpiListEditor from './KpiListEditor.vue'
import KpiTargetCards from './KpiTargetCards.vue'

/** The company's KPIs were saved, or a reading was recorded: the board beside this panel is out of date. */
const emit = defineEmits<{ changed: [] }>()

const kpis = ref<Kpi[]>([])
const draft = ref<Kpi[]>([])
const loaded = ref(false)
const editing = ref(false)
const error = ref<string | null>(null)

async function load(): Promise<void> {
    try {
        kpis.value = (await api.enterpriseKpis()).kpis
        loaded.value = true
    } catch (caught) {
        error.value = caught instanceof Error ? caught.message : 'Could not load the company KPIs.'
    }
}

function edit(): void {
    draft.value = kpis.value.map(kpi => ({ ...kpi }))
    editing.value = true
    error.value = null
}

function cancel(): void {
    editing.value = false
    error.value = null
}

/** The API is the one that refuses a half-set KPI; what it refuses is shown here rather than guessed at. */
async function save(): Promise<void> {
    error.value = null
    try {
        kpis.value = (await api.setEnterpriseKpis(draft.value)).kpis
        editing.value = false
        emit('changed')
    } catch (caught) {
        error.value = caught instanceof Error ? caught.message : 'Could not save.'
    }
}

onMounted(load)
</script>

<template>
  <section v-if="loaded" data-testid="enterprise-kpis" class="space-y-2">
    <div class="flex items-baseline justify-between gap-4">
      <p class="text-muted-foreground text-xs uppercase">Company KPIs</p>
      <Button
        v-if="!editing"
        type="button"
        size="sm"
        variant="outline"
        data-testid="enterprise-kpis-edit"
        @click="edit"
      >
        Edit
      </Button>
    </div>
    <p
      v-if="error"
      data-testid="enterprise-kpis-error"
      class="text-destructive text-sm"
    >
      {{ error }}
    </p>
    <template v-if="editing">
      <KpiListEditor v-model="draft" />
      <div class="flex gap-2">
        <Button
          type="button"
          size="sm"
          variant="outline"
          data-testid="enterprise-kpis-save"
          @click="save"
        >
          Save
        </Button>
        <Button
          type="button"
          size="sm"
          variant="ghost"
          @click="cancel"
        >
          Cancel
        </Button>
      </div>
    </template>
    <template v-else>
      <KpiTargetCards v-if="kpis.length" :kpis="kpis" @recorded="emit('changed')" />
      <p v-else class="text-muted-foreground text-sm italic">
        No company KPIs set.
      </p>
    </template>
  </section>
</template>
